import { PictrsImage } from "@components/common/pictrs-image";
import { Spinner } from "@components/common/icon";
import { Icon } from "@components/common/icon";
import { numToSI } from "@utils/helpers";
import { aimooT } from "@utils/aimoo-i18n";
import { Component, InfernoNode } from "inferno";
import { Link } from "inferno-router";
import { PostView } from "lemmy-js-client";
import {
  differenceInCalendarDays,
  differenceInHours,
  format,
  parseISO,
} from "date-fns";

type AimooCommunityPostListProps = {
  posts: PostView[];
  loading?: boolean;
};

const AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
];

function avatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_GRADIENTS.length;
  }
  return AVATAR_GRADIENTS[hash] ?? AVATAR_GRADIENTS[0];
}

/** Match community-detail prototype time labels. */
function formatAimooPostTime(published: string): string {
  try {
    const date = parseISO(published);
    const now = new Date();
    const hours = differenceInHours(now, date);
    const days = differenceInCalendarDays(now, date);

    if (hours < 1) {
      return aimooT("just_now");
    }
    if (hours < 24 && days === 0) {
      return aimooT("hours_ago", { hours });
    }
    if (days === 1) {
      return aimooT("yesterday");
    }
    if (days < 7) {
      return aimooT("days_ago", { days });
    }
    return format(date, "yyyy-MM-dd");
  } catch {
    return published;
  }
}

function postExcerpt(body?: string): string | undefined {
  if (!body) {
    return undefined;
  }
  const text = body
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_~>#-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text || undefined;
}

export class AimooCommunityPostList extends Component<
  AimooCommunityPostListProps,
  never
> {
  render(): InfernoNode {
    const { posts, loading } = this.props;

    if (loading) {
      return (
        <div className="aimoo-community-posts__loading">
          <Spinner large />
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div className="aimoo-community-posts__empty">{aimooT("no_posts")}</div>
      );
    }

    return (
      <div className="aimoo-community-posts">
        {posts.map(pv => this.renderCard(pv))}
      </div>
    );
  }

  renderCard(pv: PostView): InfernoNode {
    const { post, creator } = pv;
    const pinned = post.featured_community || post.featured_local;
    const excerpt = postExcerpt(post.body);
    const displayName = creator.display_name ?? creator.name;
    const initial = displayName.trim().charAt(0).toUpperCase() || "?";
    const postPath = `/post/${post.id}`;

    return (
      <article key={post.id} className="aimoo-community-post-card">
        {pinned && (
          <div className="aimoo-community-post-card__pin">
            <Icon icon="pin" classes="icon-inline" />
            <span>{aimooT("pinned")}</span>
          </div>
        )}

        <h3 className="aimoo-community-post-card__title">
          <Link to={postPath} className="aimoo-community-post-card__title-link">
            {post.name}
          </Link>
        </h3>

        {excerpt && (
          <p className="aimoo-community-post-card__excerpt">
            <Link to={postPath} tabIndex={-1}>
              {excerpt}
            </Link>
          </p>
        )}

        {/* Stretch link makes the whole card clickable without nesting interactive areas wrongly. */}
        <Link
          to={postPath}
          className="aimoo-community-post-card__stretch"
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="aimoo-community-post-card__meta">
          <Link
            to={`/u/${creator.name}`}
            className="aimoo-community-post-card__author"
          >
            {creator.avatar ? (
              <PictrsImage src={creator.avatar} type="icon" nsfw={false} />
            ) : (
              <span
                className={`aimoo-community-post-card__avatar ${avatarGradientClass(
                  creator.name,
                )}`}
              >
                {initial}
              </span>
            )}
            <span>{displayName}</span>
          </Link>

          <span className="aimoo-community-post-card__time">
            {formatAimooPostTime(post.published_at)}
          </span>

          <span className="aimoo-community-post-card__stat">
            <Icon icon="message-square" classes="icon-inline" />
            {numToSI(post.comments)}
          </span>

          <span className="aimoo-community-post-card__stat">
            <Icon icon="arrow-up" classes="icon-inline" />
            {numToSI(post.upvotes)}
          </span>
        </div>
      </article>
    );
  }
}
